/**
 * Brand Logo Component
 *
 * Use this component instead of hardcoding logo paths.
 * Logo paths are controlled from src/config/branding.ts
 */

import { branding } from "../config/branding";

interface BrandLogoProps {
  /** Logo variant: 'primary' for main logo, 'wordmark' for text logo */
  variant?: "primary" | "wordmark";
  /** Additional CSS classes */
  className?: string;
  /** Height (CSS value or number for pixels) */
  height?: number | string;
  /** Width (CSS value or number for pixels) */
  width?: number | string;
}

export function BrandLogo({
  variant = "primary",
  className = "",
  height,
  width,
}: BrandLogoProps) {
  // Always use the Cr8Her brand logo — never fall back to old UU crimson/noir files
  const src = variant === "primary" ? branding.logos.primary : branding.logos.wordmark;

  const alt = `${branding.company.name} Logo`;

  const style: React.CSSProperties = {};
  if (height !== undefined) {
    style.height = typeof height === "number" ? `${height}px` : height;
  }
  if (width !== undefined) {
    style.width = typeof width === "number" ? `${width}px` : width;
  }

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={Object.keys(style).length > 0 ? style : undefined}
    />
  );
}

/**
 * Cr8Her wordmark that follows the theme: pink on light and noir, white on
 * crimson. Both images render and CSS (styles/globals.css) shows the right one,
 * so it works outside the ThemeProvider too.
 */
export function ThemedWordmark({ className = "" }: { className?: string }) {
  const alt = `${branding.company.name} Logo`;
  return (
    <>
      <img src={branding.logos.wordmarkPink} alt={alt} className={`brand-wordmark-pink ${className}`} />
      <img src={branding.logos.wordmarkWhite} alt={alt} className={`brand-wordmark-white ${className}`} />
    </>
  );
}

/** Company name from branding config */
export const companyName = branding.company.name;

/** Company tagline from branding config */
export const companyTagline = branding.company.tagline;

/** Full branding config export for advanced usage */
export { branding } from "../config/branding";
